import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Relations from './Relations';
import * as Properties from './Properties';
import * as Stores from './Stores';
import * as Streams from './Streams';
import * as Objects from './Objects';

export type StringStream = Streams.ScalarStream<string, Elevated.Domain<string>>;

export type TypeParams<BindingType extends Objects.BindingType = any, StateType = any, DirectionType = any, StatusType = any, ControlType = any> = {
    Binding: BindingType;
    State: StateType;
    Direction: DirectionType;
    Status: StatusType;
    Control: ControlType;
};

export interface Case<BindingType extends { [key: string]: Objects.Binding<string> }> { 
    name: string;
    condition: (context: Instance<TypeParams<BindingType>>) => Streams.ScalarStream<boolean, Elevated.Domain<boolean>>;
    then(builder: Builder<TypeParams<BindingType>>): void;
};

export class Entrances<EntrancesTypeParams extends TypeParams> {
    in<RelationBindingType extends Objects.BindingType>(
        descriptor: Relations.Descriptor<RelationBindingType>,
        makeInstance: (entry: Relations.Instance<RelationBindingType>) => Instance<EntrancesTypeParams>
    ): void {}
};

export abstract class Builder<BuilderTypeParams extends TypeParams = TypeParams> {
    abstract instance(): Instance<BuilderTypeParams>;
    abstract zone(): Stores.Zone;
    abstract streams(): Streams.Factory;
    abstract when(case_: Case<BuilderTypeParams['Binding']>): void;

    status(value_: Streams.ValueStream<BuilderTypeParams["Status"]>): void {}
    control(options: {
        name: BuilderTypeParams["Control"],
        isEntrance?: boolean,
        isExit?: boolean,
        enable?: (binding: BuilderTypeParams["Binding"]) => Streams.ScalarStream<boolean, Elevated.Domain<boolean>>,
        confirmation?: StringStream,
        action?: (binding: BuilderTypeParams["Binding"]) => Promise<any>,
    }): void {}

    headline(cue: StringStream): void {}
    paragraphs(...text: StringStream[]): void {}
    review(cue: StringStream): void {}

    route<RouteTypeParams extends TypeParams>(
        forward: BuilderTypeParams["State"],
        descriptor: Descriptor<RouteTypeParams>, 
        makeInstance: (binding: BuilderTypeParams["Binding"]) => Instance<RouteTypeParams>,
        backward?: RouteTypeParams["Direction"]
    ): void {}
    detour<DetourTypeParams extends TypeParams>(
        descriptor: Descriptor<DetourTypeParams>, 
        makeInstance: (binding: BuilderTypeParams["Binding"]) => Instance<DetourTypeParams>,
    ): void {}
        
    property<PropertyTypeParams extends Properties.TypeParams>(
        stream: Streams.PropertyStream<PropertyTypeParams>
    ): void {}
    relation<RelationBindingType extends Objects.BindingType>(
        stream: Streams.RelationStream<RelationBindingType>
    ): void {}
};

export abstract class Descriptor<DescriptorTypeParams extends TypeParams = TypeParams> {
    abstract canonicalName: string;
    abstract initialize(): {
        binding(default_: Partial<DescriptorTypeParams["Binding"]>): DescriptorTypeParams["Binding"],
        state(default_: Partial<DescriptorTypeParams["State"]>): DescriptorTypeParams["State"];
    }
    abstract enter(entrances: Entrances<DescriptorTypeParams>): void;
    abstract build(builder: Builder<DescriptorTypeParams>): void;
    bind(
        _binding: Partial<DescriptorTypeParams["Binding"]>, 
        _state: Partial<DescriptorTypeParams["State"]> = {}
    ): Instance<DescriptorTypeParams> {
        return { 
            descriptor: this, 
            binding: this.initialize().binding(_binding), 
            state: this.initialize().state(_state) 
        }
    }
    makeEntrance(
        from: Relations.Instance<any>
    ) {
        const result: Instance<DescriptorTypeParams>[] = [];
        this.enter(new class extends Entrances<DescriptorTypeParams> {
            in<RelationBindingType extends Objects.BindingType>(
                via: Relations.Descriptor<RelationBindingType>, 
                makeContext: (entry: Relations.Instance<RelationBindingType>) => Instance<DescriptorTypeParams>
            ): void {
                if(from.descriptor === via) {
                    result.push(makeContext(from));
                }
            }
        })
        return result;
    }
}

export interface Instance<InstanceTypeParams extends TypeParams> {
    descriptor: Descriptor<InstanceTypeParams>;
    binding: InstanceTypeParams["Binding"];
    state: InstanceTypeParams["State"];
}
